import {
  getDatasetDirectoryTree,
  listDatasetEpisodes,
  listDatasetFiles,
  listEpisodeFiles,
  signDatasetFiles,
  type DatasetEpisodeListQuery,
  type DatasetFile,
  type DatasetFileListQuery,
  type DatasetFileRef,
  type DirectoryTree,
  type EpisodeFile,
  type EpisodeSummary,
  type PageResp,
  type SignedDatasetFile,
} from "@/api";
import { datasetExtraFileWriteApi, type ExtraFileWriteApi } from "./extraFilesApi";

// 정본(directory-tree 기반) 파일 브라우저가 쓰는 리소스 seam. 읽기는 카탈로그(episode·meta·extra)와
// 관계없이 동작한다. 폴더 파일은 한 목록으로 받고, URL 은 `(kind, uuid)` 배치 서명으로 받는다.
// 쓰기(declare/finalize/rename/remove)는 extra 파일에만 있다. seam 이 있으면 브라우저가 편집을
// 표시한다. 에피소드는 EpisodeSummary 로 정규화한다.
export type FileBrowserSource = ExtraFileWriteApi & {
  // canonical(채널/episode)과 extra 를 병합한 디렉터리 스켈레톤. 파일은 포함하지 않는다.
  directoryTree: (uuid: string) => Promise<DirectoryTree>;
  // 에피소드 page(EpisodeSummary 로 정규화). Files 트리의 episode 폴더를 lazy 조회한다.
  // 리뷰 필터 축은 서버가 소유하므로 query 를 그대로 전달한다. 클라이언트는 결과를 다시 거르지 않는다.
  listEpisodes: (uuid: string, query: DatasetEpisodeListQuery) => Promise<PageResp<EpisodeSummary>>;
  // 선택 에피소드의 파일(영상/parquet). 우측 영상 패널에 쓴다. EpisodeSummary.dataset_uuid 스코프로 조회한다.
  episodeFiles: (episode: EpisodeSummary) => Promise<EpisodeFile[]>;
  // 폴더 하나의 직속 파일 page. 서버가 세 카탈로그를 합치고 항목마다 `kind` 를 넣어 준다.
  // 업로드 다이얼로그는 같은 조회를 `filename` probe 로 써서 덮어쓰기와 충돌을 미리 확인한다.
  folderFiles: (datasetUuid: string, query: DatasetFileListQuery) => Promise<PageResp<DatasetFile>>;
  // 파일 묶음의 1회용 signed GET URL. 미리보기와 다운로드는 파일 한 개짜리 배치로 요청한다.
  signFiles: (
    datasetUuid: string,
    files: readonly DatasetFileRef[],
  ) => Promise<SignedDatasetFile[]>;
};

export const datasetFileBrowserSource: FileBrowserSource = {
  ...datasetExtraFileWriteApi,
  directoryTree: getDatasetDirectoryTree,
  listEpisodes: listDatasetEpisodes,
  episodeFiles: (episode) => listEpisodeFiles(episode.dataset_uuid, episode.uuid),
  folderFiles: listDatasetFiles,
  signFiles: signDatasetFiles,
};

// 한 사람이 오가는 페이지 수는 적다. 상한을 넘으면 오래된 페이지를 버리고 다시 요청한다.
const SHARED_EPISODE_PAGE_MAX_ENTRIES = 8;

/**
 * `listEpisodes` 응답을 요청 조건별로 나눠 쓰는 source 를 만든다.
 *
 * Files 트리의 채널 폴더는 폴더마다 같은 에피소드 page 를 보인다. dataset 과 query 가 같은
 * 요청은 한 번만 보내고 응답을 함께 쓴다(rules.md §7). 다른 메서드는 그대로 전달한다.
 *
 * 응답은 반환한 source 가 살아 있는 동안 보관한다. 그래서 호출자가 수명을 정한다. 목록이 바뀔 수
 * 있는 화면(리뷰 저장 뒤 제자리 재조회)은 이 source 를 쓰지 않는다.
 */
export function withSharedEpisodePages(source: FileBrowserSource): FileBrowserSource {
  const pages = new Map<string, Promise<PageResp<EpisodeSummary>>>();
  return {
    ...source,
    listEpisodes: (uuid, query) => {
      const key = JSON.stringify([uuid, query]);
      const shared = pages.get(key);
      if (shared !== undefined) {
        return shared;
      }
      const request = source.listEpisodes(uuid, query);
      pages.set(key, request);
      if (pages.size > SHARED_EPISODE_PAGE_MAX_ENTRIES) {
        const oldest = pages.keys().next().value;
        if (oldest !== undefined) {
          pages.delete(oldest);
        }
      }
      // 실패는 보관하지 않는다. 그래서 재시도가 다시 요청한다. 그 사이 같은 조건의 새 요청이
      // 자리를 차지했으면 그 요청은 남긴다.
      request.catch(() => {
        if (pages.get(key) === request) {
          pages.delete(key);
        }
      });
      return request;
    },
  };
}
